import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { booksService } from '@/services/books.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { BookView } from '@/types/api';

/**
 * VocabularyBookmarkUploader — sibling of `BookCoverUploader` for the
 * per-book "vocabulary bookmark" image surfaced on the reader's book
 * detail "Resources" tab. Slotted into the admin book editor's left
 * column directly under the cover uploader.
 *
 * Same controlled-component pattern as the cover uploader: parent owns
 * the `BookView`, we POST the chosen file via
 * `booksService.uploadVocabularyBookmark` and hand the refreshed payload
 * back through `onBookChange`.
 *
 * When no bookmark is set, the card collapses to a single CTA. When one
 * is set, we show a preview thumbnail plus Replace / Remove / Open
 * affordances.
 */
export interface VocabularyBookmarkUploaderProps {
  book: BookView;
  onBookChange: (book: BookView) => void;
  className?: string;
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB — mirrors BookCoverUploader

export function VocabularyBookmarkUploader({
  book,
  onBookChange,
  className,
}: VocabularyBookmarkUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const busy = uploading || removing;

  const handlePick = () => {
    if (busy) return;
    inputRef.current?.click();
  };

  const handleFile = async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Vocabulary bookmark must be a PNG, JPEG, WEBP, or GIF image.');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error('Vocabulary bookmark files must be 5MB or smaller.');
      return;
    }
    setUploading(true);
    try {
      const updated = await booksService.uploadVocabularyBookmark(book.id, file);
      onBookChange(updated);
      toast.success('Vocabulary bookmark updated.');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not upload the vocabulary bookmark.',
      );
    } finally {
      setUploading(false);
      // Reset the input so picking the same file again still fires `onChange`.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (busy || !book.vocabularyBookmark) return;
    setRemoving(true);
    try {
      const updated = await booksService.deleteVocabularyBookmark(book.id);
      onBookChange(updated);
      toast.success('Vocabulary bookmark removed.');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not remove the vocabulary bookmark.',
      );
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className={cn('rounded-2xl border border-or-slate-200 bg-white p-5 shadow-sm', className)}>
      <div className="or-kicker text-or-slate-500">Vocabulary bookmark</div>
      <div className="mt-3 flex items-start gap-5">
        <div className="shrink-0">
          {book.vocabularyBookmark ? (
            <img
              src={book.vocabularyBookmark}
              alt={`${book.title} vocabulary bookmark`}
              width={160}
              height={230}
              className="h-[230px] w-[160px] rounded-xl border border-or-slate-100 object-contain shadow-or-card"
            />
          ) : (
            <div
              className="flex h-[230px] w-[160px] flex-col items-center justify-center rounded-xl border border-dashed border-or-slate-300 bg-or-cream-50 px-3 text-center text-[11px] text-or-slate-500"
              aria-hidden
            >
              No bookmark uploaded yet
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <p className="text-[12px] leading-relaxed text-or-slate-600">
            {book.vocabularyBookmark
              ? 'Replace the current vocabulary bookmark or remove it. Readers see this on the book’s Resources tab.'
              : 'Upload a PNG, JPEG, WEBP, or GIF bookmark. Max 5MB. Readers see this on the book’s Resources tab.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" size="sm" onClick={handlePick} disabled={busy}>
              {uploading ? 'Uploading…' : book.vocabularyBookmark ? 'Replace' : 'Upload bookmark'}
            </Button>
            {book.vocabularyBookmark ? (
              <>
                <a
                  href={book.vocabularyBookmark}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full border border-or-slate-200 bg-white px-3 py-1 text-[12px] font-semibold text-or-slate-700 hover:bg-or-slate-50"
                  title="Open the bookmark file in a new tab"
                >
                  <ExternalLink className="h-3 w-3" aria-hidden />
                  Open
                </a>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleRemove}
                  disabled={busy}
                  title="Clear the uploaded bookmark"
                >
                  {removing ? 'Removing…' : 'Remove'}
                </Button>
              </>
            ) : null}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
        </div>
      </div>
    </div>
  );
}
